import { Fragment, useEffect, useRef } from 'react';
import type { Log } from '../../worker/log-worker-types.ts';
import { DebugLine } from './DebugLine.tsx';
import { useIsAtBottom } from './useIsAtBottom.ts';
import styles from './LogStream.module.css';

type Props = {
  commandResults: Array<[number, Log]>;
  streamStateMessages: Array<[number, string]>;
  logs: Array<Log | string>;
};

export function LogStream({ commandResults, streamStateMessages, logs }: Props) {
  return (
    <PinToBottomContainer>
      {/* eslint-disable react/no-array-index-key */}
      {/* Show any command results and stream state messages from before logs started streaming */}
      {commandResults
        .filter(([i]) => i === 0)
        .map(([, r], index) => (
          <DebugLine log={r} key={`command-${index}`} variant="command" />
        ))}
      {streamStateMessages
        .filter(([i]) => i === 0)
        .map(([, s], index) =>
          s ?
            <div style={{ height: 17 }} key={`state-${index}`}>
              {s}
            </div>
          : <div style={{ height: 17 }} key={`state-${index}`}>
              &nbsp;
            </div>
        )}
      {logs.map((l, index) => (
        <Fragment key={`log-${index}`}>
          <DebugLine log={l} />
          {/* Show status messages that were added after streaming ended */}
          {streamStateMessages
            .filter(([i]) => i === index + 1)
            .map(([, s], idx) =>
              s ?
                <div style={{ height: 17 }} key={`state-${idx}`}>
                  {s}
                </div>
              : <div style={{ height: 17 }} key={`state-${idx}`}>
                  &nbsp;
                </div>
            )}
          {/* Show command results that were added after streaming ended */}
          {commandResults
            .filter(([i]) => i === index + 1)
            .map(([, r], idx) => (
              <DebugLine log={r} key={`command-${idx}`} variant="command" />
            ))}
        </Fragment>
      ))}
      {/* eslint-enable react/no-array-index-key */}
    </PinToBottomContainer>
  );
}

/** {@link PinToBottomContainer} scrolls to the bottom when its children update */
export function PinToBottomContainer({ children }: { children: React.ReactNode[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAtBottom = useIsAtBottom(containerRef);

  useEffect(() => {
    if (!containerRef.current) return;
    if (isAtBottom)
      containerRef.current.scrollTop = containerRef.current.scrollHeight - containerRef.current.clientHeight;
  }, [children, isAtBottom]);

  return (
    <div ref={containerRef} className={styles.LogStream}>
      <div>{children}</div>
    </div>
  );
}
